import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import type { Device } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { errorText } from '../../lib/useRelay';

interface Props {
  /** The device to act on; null = closed. */
  device: Device | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}

/** `PUT /devices/:id/name`. The server normalises the name; a taken one (409) is shown on the field. */
export function RenameDialog({ device, onClose, onDone }: Props) {
  const { api } = useAuth();
  const [name, setName] = useState('');
  const [taken, setTaken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!device) return;
    setName(device.name);
    setTaken(null);
    setError(null);
  }, [device]);

  const save = async () => {
    if (!device) return;
    setSaving(true);
    setTaken(null);
    setError(null);
    try {
      await api.renameDevice(device.id, name.trim());
      await onDone();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'name_taken') setTaken(e.message);
      else setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={device !== null}
      onOpenChange={(_e, data) => {
        if (!data.open && !saving) onClose();
      }}
    >
      <DialogSurface>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <DialogBody>
            <DialogTitle>Rename device</DialogTitle>
            <DialogContent>
              <Field
                label="Name"
                hint={taken ? undefined : 'Also its DNS name: <name>.vpn inside the overlay.'}
                validationMessage={taken}
                validationState={taken ? 'error' : 'none'}
              >
                <Input value={name} onChange={(_e, data) => setName(data.value)} autoFocus />
              </Field>
              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={saving} onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                appearance="primary"
                disabled={saving || !name.trim() || name.trim() === device?.name}
                icon={saving ? <Spinner size="tiny" /> : undefined}
              >
                Rename
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}

/** `DELETE /devices/:id`, confirmed first. A 404 means it is already gone: resync instead of failing. */
export function RevokeDialog({ device, onClose, onDone }: Props) {
  const { api } = useAuth();
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [device]);

  const confirm = async () => {
    if (!device) return;
    setRevoking(true);
    setError(null);
    try {
      await api.revokeDevice(device.id);
      await onDone();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'not_found') {
        onClose();
        void onDone();
      } else {
        setError(errorText(e));
      }
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog
      open={device !== null}
      onOpenChange={(_e, data) => {
        if (!data.open && !revoking) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Revoke device?</DialogTitle>
          <DialogContent>
            <Text>
              <strong>{device?.name}</strong> ({device?.overlayIp}) will lose access immediately and its overlay IP is
              freed. The device has to be enrolled again to reconnect.
            </Text>
            {error && (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={revoking} onClick={onClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={revoking}
              icon={revoking ? <Spinner size="tiny" /> : undefined}
              onClick={() => void confirm()}
            >
              Revoke
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
